import { urls } from '../config/configConstants';
import { MAINTENANCE } from './constants/routes';
import { useEffect, useState } from 'react';
import { alert, muteDefaultError, NotificationMode } from '@components/common/snackbarNotifications/Notifications';
import t from '@i18n/t';
import gaAnalytics from '../config/gaAnalytics';
import { clarityEvent } from '../config/clarity';

/**
 * When to show the maintenance page, and when to leave it.
 *
 * A request that gets no answer (or a 502, 503, 504) used to send the student there at once. Most of those were the
 * student's own internet, gone for a few seconds while a laptop wakes up: the tab asks Shovel something the moment it
 * is back on screen, before the Wi-Fi is. So one failed request now only starts a check: wait for the internet, then
 * ask Shovel a few times. The page shows only when the student is online and Shovel still doesn't answer.
 *
 * When it's the student's own internet, the app says so instead (components/common/OfflineBar.tsx, and
 * pages/OfflinePage.tsx before the app has loaded) and checks back on its own, like Gmail and Slack do.
 */

// Set at build time for planned maintenance: the page stays, whatever the server says.
export const plannedMaintenance = process.env.REACT_APP_IS_MAINTENANCE === 'true';

const ATTEMPTS = 3;
const RETRY_MS = 5_000;
const OFFLINE_RETRY_MS = 3_000;
const PROBE_TIMEOUT_MS = 5_000;
const FROM_KEY = 'maintenanceFrom';
const RELOADED_KEY = 'maintenanceReloadedAt';
// A request that never succeeds (blocked by an extension, say) must not reload or re-read in a loop.
const AGAIN_AFTER_MS = 60_000;
// Offline, how long to wait before each new check: sooner at first, then every half minute.
const OFFLINE_CHECKS_MS = [3_000, 5_000, 10_000, 30_000];
const NOT_SAVED_SHOWN_MS = 4_000;
// Long enough to see that "Try again" did something, when the check answers at once.
export const SPINNER_MIN_MS = 1_200;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Resolves at once when the browser says it's online, else when it comes back (or after a while, in case it never says).
const online = () =>
  navigator.onLine
    ? Promise.resolve()
    : new Promise<void>(resolve => {
        const done = () => {
          window.removeEventListener('online', done);
          resolve();
        };
        window.addEventListener('online', done);
        setTimeout(done, OFFLINE_RETRY_MS);
      });

const reaches = async (url: string, isUp: (response: Response) => boolean) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return isUp(await fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal }));
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

// Any answer from the backend itself means it's up: unsigned, this one answers 401 (with CORS headers, so it can be
// read). The health endpoint has none. When the server is down, the load balancer's 502/503 has no CORS headers
// either, so it reads as no answer.
export const backendUp = () => reaches(`${urls.backend}/account`, response => response.status < 500);

// Our own site answering tells "Shovel is down" from "this computer is offline". Not a .png (the service worker keeps
// copies of those), and the query string keeps it out of the precache.
const internetUp = () => reaches(`/favicon.ico?probe=${Date.now()}`, response => response.ok);

let appLoaded = false;
let refreshData = () => {};
let refreshedAt = 0;

const reloadOnce = () => {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED_KEY) || 0) < AGAIN_AFTER_MS) return;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
};

const refreshOnce = () => {
  if (Date.now() - refreshedAt < AGAIN_AFTER_MS) return;
  refreshedAt = Date.now();
  refreshData();
};

/** App.tsx tells whether the app finished loading, and how to read its data again. */
export const setAppLoaded = (loaded: boolean) => {
  appLoaded = loaded;
};
export const setRefreshData = (refresh: () => void) => {
  refreshData = refresh;
};

// The student's own internet: off, being checked, and whether a save was just lost to it.
type Connection = { offline: boolean; checking: boolean; notSaved: boolean };

let connection: Connection = { offline: false, checking: false, notSaved: false };
const connectionListeners = new Set<() => void>();
const setConnection = (change: Partial<Connection>) => {
  connection = { ...connection, ...change };
  connectionListeners.forEach(listener => listener());
};

export const useConnection = () => {
  const [state, setState] = useState(connection);
  useEffect(() => {
    const listener = () => setState(connection);
    connectionListeners.add(listener);
    listener();
    return () => {
      connectionListeners.delete(listener);
    };
  }, []);
  return state;
};

let offlineSince = 0;
let savesLost = 0;
// Most offline spells happen in tabs left open behind others on a sleeping laptop (≈7,500 a day, 2026-10-28), which no
// student sees. Such a spell is not counted, and the tab catches up once it is looked at again.
const SEEN_AFTER_MS = 2_000; // as long as the bar takes to show (OfflineBar)
let seenOffline = false;
let seenTimeout: number | undefined;
let catchUpWhenVisible = false;
let nextCheck = 0;
let checkTimeout: number | undefined;
let notSavedTimeout: number | undefined;

const scheduleCheck = () => {
  window.clearTimeout(checkTimeout);
  checkTimeout = window.setTimeout(
    () => checkConnection(),
    OFFLINE_CHECKS_MS[Math.min(nextCheck++, OFFLINE_CHECKS_MS.length - 1)]
  );
};

const visible = () => document.visibilityState === 'visible';

// The spell counts once the tab has been on screen for as long as the bar takes to show.
const watchSeen = () => {
  window.clearTimeout(seenTimeout);
  if (connection.offline && visible()) {
    seenTimeout = window.setTimeout(() => {
      seenOffline = true;
    }, SEEN_AFTER_MS);
  }
};

const goOffline = () => {
  if (connection.offline) return;
  offlineSince = Date.now();
  savesLost = 0;
  seenOffline = false;
  nextCheck = 0;
  setConnection({ offline: true });
  watchSeen();
  scheduleCheck();
};

// What failed meanwhile is read again, at most once a minute: if part of that read fails again and the bar comes back,
// reading everything at every return would go round in circles (a Safari tab did, 444 times in 6 hours, 2026-10-24).
// Before the app has loaded, it waits for answers that never came: a reload, a moment after the event above goes out.
const catchUp = () => {
  catchUpWhenVisible = false;
  if (appLoaded) refreshOnce();
  else window.setTimeout(() => window.location.reload(), 500);
};

const backOnline = () => {
  window.clearTimeout(checkTimeout);
  window.clearTimeout(notSavedTimeout);
  window.clearTimeout(seenTimeout);
  // Sent now: while offline it couldn't be. Only spells the student saw, or lost a save to.
  if (seenOffline || savesLost) {
    gaAnalytics.track('went_offline', {
      seconds: Math.round((Date.now() - offlineSince) / 1000),
      at_startup: !appLoaded,
      saves_lost: savesLost
    });
    clarityEvent('went_offline');
  }
  setConnection({ offline: false, checking: false, notSaved: false });
  if (visible()) catchUp();
  else catchUpWhenVisible = true;
};

// Back once Shovel answers, or at least our own site does: Shovel itself being down is the maintenance page's business,
// and a request failing then takes the student there. Only "Try again" shows its spinner; the timed checks are quiet.
let probing = false;
const checkConnection = async (byStudent = false) => {
  if (!connection.offline || probing) return;
  probing = true;
  if (byStudent) setConnection({ checking: true });
  const [back] = await Promise.all([
    (async () => (await backendUp()) || (await internetUp()))(),
    wait(byStudent ? SPINNER_MIN_MS : 0)
  ]);
  probing = false;
  setConnection({ checking: false });
  if (back) backOnline();
  else scheduleCheck();
};

/** "Try again" on the offline bar and the offline page. */
export const tryAgain = () => {
  nextCheck = 0;
  checkConnection(true);
};

const notSavedOffline = () => {
  savesLost++;
  setConnection({ notSaved: true });
  window.clearTimeout(notSavedTimeout);
  notSavedTimeout = window.setTimeout(() => setConnection({ notSaved: false }), NOT_SAVED_SHOWN_MS);
};

// The browser's word alone isn't enough: some computers say "offline" with the internet working (a VPN, a virtual network
// adapter), and the bar then stayed up over a working calendar (a student, 2026-10-29). Offline only if neither Shovel
// nor our own site answers; truly offline, both fail at once.
const goOfflineIfNoAnswer = async () => {
  if (!(await backendUp()) && !(await internetUp())) goOffline();
};
window.addEventListener('offline', goOfflineIfNoAnswer);
document.addEventListener('visibilitychange', () => {
  watchSeen();
  if (visible() && catchUpWhenVisible) catchUp();
});
window.addEventListener('online', () => {
  nextCheck = 0;
  checkConnection();
});
if (!navigator.onLine) goOfflineIfNoAnswer();

/** An answer from Shovel while the bar is up: probably back, so check now. Not taken as it is: a request already on its
 * way when the internet went, or one the browser answered from its cache, would end the bar too soon. */
export const onRequestAnswered = () => {
  if (!connection.offline) return;
  nextCheck = 0;
  checkConnection();
};

/** Waits while the browser says it's offline. Shovel answers: 'up'. No internet at all: 'offline'. The internet works
 * but Shovel didn't answer three times: 'down'. */
const shovelAnswers = async () => {
  for (let strike = 0; ; ) {
    await online();
    if (await backendUp()) return 'up';
    if (!(await internetUp())) return 'offline';
    if (++strike === ATTEMPTS) return 'down';
    await wait(RETRY_MS);
  }
};

const onMaintenancePage = () => window.location.pathname === MAINTENANCE;

let checking = false;
let notSavedAt = 0;

/** A request on the backend instance got no answer, or a 502/503/504. A change of the student's is said to be lost. */
// A save that got no answer: the offline bar says it, else one message. Several failing together say so once.
const sayNotSaved = () => {
  if (connection.offline) notSavedOffline();
  else if (Date.now() - notSavedAt > RETRY_MS) {
    notSavedAt = Date.now();
    alert(t.NOT_SAVED_NO_ANSWER, NotificationMode.ERROR, 5000);
  }
};

/**
 * No answer from one of Shovel's other services (events, the AI, the lambdas). They don't decide the maintenance page,
 * but offline the bar speaks for them as for the main server. The events service's saves (moving a calendar event, say)
 * have nothing better to say than "Something went wrong", so theirs is replaced online too (`replaceOnline`); the
 * others may say something of their own, so they are left alone while online.
 */
let probingOther = false;

export const onOtherRequestFailed = (wasChange: boolean, replaceOnline: boolean) => {
  if (connection.offline || replaceOnline) {
    muteDefaultError();
    if (wasChange) sayNotSaved();
  }
  // Online by the browser's word, but maybe not really (a laptop just woke up). Offline only if neither our own site nor
  // the main server answers: one service not answering is not the student's internet.
  if (!connection.offline && !probingOther) {
    probingOther = true;
    (async () => !(await internetUp()) && !(await backendUp()))()
      .then(gone => {
        if (gone) goOffline();
      })
      .finally(() => {
        probingOther = false;
      });
  }
};

export const onRequestFailed = async (wasChange = false) => {
  // "Something went wrong, please reload" never helps here: the offline bar, the message below or the maintenance page
  // says what happened.
  muteDefaultError();
  if (wasChange) sayNotSaved();
  // Offline, the connection check above takes it from here. On the maintenance page itself, its own check decides when
  // to leave; another redirect would only reload it.
  if (connection.offline || checking || onMaintenancePage()) return;
  checking = true;
  try {
    const answer = await shovelAnswers();
    if (answer === 'offline') {
      goOffline();
      return;
    }
    if (answer === 'up') {
      // Whatever failed meanwhile is read again. Before the app has loaded, it waits for answers that never came.
      if (appLoaded) refreshOnce();
      else reloadOnce();
      return;
    }
    try {
      sessionStorage.setItem(FROM_KEY, window.location.pathname + window.location.search);
    } catch {}
    window.location.assign(MAINTENANCE);
  } finally {
    checking = false;
  }
};

/** Back to the page the student was on before the maintenance page. */
export const leaveMaintenancePage = () => {
  let from: string | null = null;
  try {
    from = sessionStorage.getItem(FROM_KEY);
    sessionStorage.removeItem(FROM_KEY);
  } catch {}
  window.location.replace(from && from !== MAINTENANCE ? from : '/');
};
